import { Phone, MapPin, Menu, X } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { ResponsiveImage } from "@/components/site/ResponsiveImage";
import { business } from "@/lib/business";
import { imageSizes, logoImage } from "@/lib/images";

type NavItem =
  | { label: string; to: "/services" | "/about" | "/contact" }
  | { label: string; to: "/"; hash: string };

const nav: NavItem[] = [
  { label: "The Shop", to: "/", hash: "shop" },
  { label: "Gallery", to: "/", hash: "gallery" },
  { label: "Services", to: "/services" },
  { label: "About", to: "/about" },
  { label: "Reviews", to: "/", hash: "reviews" },
  { label: "FAQ", to: "/", hash: "faq" },
  { label: "Contact", to: "/contact" },
];

const navLinkClass =
  "inline-flex min-h-11 items-center text-sm font-semibold uppercase tracking-wider transition-colors hover:text-foreground";

export function Header() {
  const [open, setOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
        menuButtonRef.current?.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  return (
    <header className="site-header sticky top-0 z-50 border-b border-border bg-background/90 backdrop-blur">
      <div className="header-topbar hidden items-center justify-center gap-6 bg-secondary py-2 text-xs tracking-wide text-muted-foreground md:flex">
        <a
          href={business.mapsUrl}
          className="inline-flex items-center gap-2 underline-offset-4 hover:text-foreground hover:underline"
        >
          <MapPin className="size-3.5 text-primary-text" aria-hidden="true" />
          {business.address}, {business.city}, {business.state} {business.zip}
        </a>
        <span>{business.hours}</span>
      </div>
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3">
        <Link to="/" className="flex items-center gap-3" activeOptions={{ exact: true }}>
          <ResponsiveImage
            image={logoImage}
            alt=""
            sizes={imageSizes.logoHeader}
            loading="eager"
            pictureClassName="w-12 shrink-0 sm:w-14"
            imgClassName="size-12 shrink-0 rounded-sm bg-steel p-0.5 sm:size-14"
          />
          <span className="font-display text-lg leading-none sm:text-2xl">
            Champion Automotive
            <span className="block text-xs tracking-[0.28em] text-muted-foreground sm:text-sm">
              Machine Shop
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {nav.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              {...("hash" in item ? { hash: item.hash } : {})}
              activeOptions={"hash" in item ? { exact: true, includeHash: true } : { exact: true }}
              className={navLinkClass}
              activeProps={{ className: "text-primary-text", "aria-current": "page" }}
              inactiveProps={{ className: "text-muted-foreground" }}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={business.phoneHref}
            className="btn-base btn-primary hidden sm:inline-flex"
            aria-label={`Call Champion Automotive Machine Shop at ${business.phone}`}
          >
            <Phone className="size-4" aria-hidden="true" />
            {business.phone}
          </a>
          <button
            ref={menuButtonRef}
            type="button"
            onClick={() => setOpen((value) => !value)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="site-menu"
            className="inline-flex size-11 items-center justify-center rounded-md border border-border lg:hidden"
          >
            {open ? (
              <X className="size-5" aria-hidden="true" />
            ) : (
              <Menu className="size-5" aria-hidden="true" />
            )}
          </button>
        </div>
      </div>

      <nav
        id="site-menu"
        hidden={!open}
        className="border-t border-border px-4 py-3 lg:hidden"
        aria-label="Mobile"
      >
        <ul className="grid gap-1">
          {nav.map((item) => (
            <li key={item.label}>
              <Link
                to={item.to}
                {...("hash" in item ? { hash: item.hash } : {})}
                activeOptions={
                  "hash" in item ? { exact: true, includeHash: true } : { exact: true }
                }
                onClick={() => setOpen(false)}
                className="flex min-h-11 items-center rounded-md px-2 text-sm font-semibold uppercase tracking-wider hover:bg-secondary hover:text-foreground"
                activeProps={{ className: "text-primary-text", "aria-current": "page" }}
                inactiveProps={{ className: "text-muted-foreground" }}
              >
                {item.label}
              </Link>
            </li>
          ))}
          <li className="pt-2">
            <a href={business.phoneHref} className="btn-base btn-primary w-full">
              <Phone className="size-4" aria-hidden="true" />
              Call {business.phone}
            </a>
          </li>
        </ul>
      </nav>
    </header>
  );
}
